import { Media } from "@/payload-types"
import { GalleryItem } from 'unmuted-lightgallery/dist/lg-utils'
import { VideoStatus } from "@/enum/enum"
import { getVideoSource, VideoSource } from '@/components/media/getVideoSource'

export interface EmbeddedVideoItem {
    source: 'vimeo' | 'youtube' | 'facebook'
    videoId: string
    title: string
    caption?: string | null
    id?: string | null
    timestamp?: number | null
    faceThumb?: Media
}

const payload_url = process.env.NEXT_PUBLIC_PAYLOAD_URL ?? ""

function splitImageOrVideo(item: Media, timeStamp?: string) {
    if (item && typeof item === 'object') {
        if (item.mimeType?.startsWith('image')) {
            return {
                src: `${payload_url}${item.sizes?.webp?.url}`,
                thumb: `${payload_url}${item.sizes?.thumbnail?.url}`,
                title: item.title,
            }
        } else {
            return {
                video: {
                    source: [
                        {
                            src: `${payload_url}${item.url}?#t=${timeStamp}`,
                            type: 'video/mp4',
                            poster: `${payload_url}${item.coverImage && typeof item.coverImage === 'object'
                                ? item.coverImage.sizes?.large?.url
                                : ''
                                }`,
                        },
                    ],
                    attributes: {
                        preload: 'none',
                        controls: true,
                        'data-start-time': timeStamp ? Number(timeStamp) : 0,
                    },
                    tracks: [],
                },
                thumb: `${payload_url}${item.coverImage && typeof item.coverImage === 'object'
                    ? item.coverImage.sizes?.thumbnail?.url
                    : ''
                    }`,
            }
        }
    }
    return null
}
export const getYoutubeSource = (videoId: string, timestamp?: number) => {
    return `https://www.youtube.com/watch?v=${videoId}${timestamp ? '&t=' + timestamp : ''}`
}
export const getYoutubeThumbnail = (videoId: string) => {
    return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
}

export const getVimeoSource = (videoId: string, timestamp?: number) => {
    return `https://vimeo.com/${videoId}${timestamp ? '#t=' + timestamp : ''}`
}
export const getVimeoThumbnail = (videoId: string) => {
    return `https://vumbnail.com/${videoId}.jpg`
}

export const getFacebookSource = (videoId: string) => {
    return `https://www.facebook.com/${videoId}`
}
export const getFacebookThumbnail = (coverImage?: Media, size: 'original' | 'thumb' | 'medium' = 'original') => {
    if (coverImage) {
        if (size === 'thumb') {
            return `${payload_url}${coverImage.sizes?.thumbnail?.url}`
        }
        else if (size === 'medium') {
            return `${payload_url}${coverImage.sizes?.medium?.url}`
        }
        return `${payload_url}${coverImage.sizes?.webp?.url}`
    }
    return ''
}

export const normalizedGallery = (
    gallery: (string | Media | EmbeddedVideoItem)[] | null,
    timeStamps?: {
        value: string
        mediaId: string
    }[]
): GalleryItem[] => {
    return (
        gallery
            ?.map((item) => {
                if (!item) return null

                // Plain image URL
                if (typeof item === 'string') {
                    return { src: item, subHtml: '' }
                }

                //youtube
                if ('videoId' in item && item.source === 'youtube') {
                    return {
                        src: getYoutubeSource(item.videoId, item?.timestamp ?? undefined),
                        subHtml: item.title ? `<h4>${item.title}</h4>` : '',
                        thumb: getYoutubeThumbnail(item.videoId),
                    }
                }

                // Vimeo
                if ('videoId' in item && item.source === 'vimeo') {
                    return {
                        src: getVimeoSource(item.videoId, item?.timestamp ?? undefined),
                        subHtml: item.title ? `<h4>${item.title}</h4>` : '',
                        thumb: getVimeoThumbnail(item.videoId),
                    }
                }

                //facebook
                if ('videoId' in item && item.source === 'facebook') {
                    return {
                        src: getFacebookSource(item.videoId),
                        subHtml: item.title ? `<h4>${item.title}</h4>` : '',
                        thumb: getFacebookThumbnail(item.faceThumb, 'thumb'),
                        poster: getFacebookThumbnail(item.faceThumb, 'original'),
                    }
                }

                const timeStamp = timeStamps?.find(
                    (ts) => ts.mediaId === item.id,
                )?.value

                const videoSource = getVideoSource(item as Media)

                // Mux Video
                if (videoSource == VideoSource.MUX) {
                    return getMuxVideoSource(item as Media, timeStamp)
                }

                // Payload CMS Media (image or video file)
                if (videoSource == VideoSource.CUSTOM) {
                    const result = splitImageOrVideo(item as Media, timeStamp)
                    if (result) {
                        return result as GalleryItem
                    }
                }

                return null
            })
            ?.filter((item): item is GalleryItem => item !== null) ?? []
    )
}

function getMuxVideoSource(item: Media, timeStamp?: string) {
    if (item.mux?.status === VideoStatus.READY) {
        const videoSource = `https://stream.mux.com/${item.mux?.playbackId}.m3u8`
        return {
            video: {
                source: [
                    {
                        src: videoSource,
                        type: 'application/x-mpegURL',
                        poster: `${payload_url}${item.coverImage && typeof item.coverImage === 'object' ? item.coverImage.sizes?.webp?.url : ''}`,
                    },
                ],
                attributes: {
                    preload: 'none',
                    controls: true,
                    'data-start-time': timeStamp ? Number(timeStamp) : 0,
                },
                tracks: [],
            },
            thumb: `${payload_url}${item.coverImage && typeof item.coverImage === 'object' ? item.coverImage.sizes?.thumbnail?.url : ''}`,
        }
    } else if (item.mux?.status === VideoStatus.PENDING) {
        return { 
            src: '/images/video-in-progress.png', 
            subHtml: '',
            thumb: '/images/video-in-progress.png' 
        }
    } else if (item.mux?.status === VideoStatus.FAILED) {
        return { 
            src: '/images/video-failed.png', 
            subHtml: '',
            thumb: '/images/video-failed.png' 
        }
    }
    return null
}
